import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../lib/api';

export interface CredentialItem {
  id: string;
  name: string;
  used_as: 'primary' | 'mfa';
  discoverable: boolean;
  created_at: string;
  last_used_at: string | null;
}

interface ErrorBody {
  error?: string;
  message?: string;
}

export interface CredentialsError extends Error {
  status?: number;
  body?: ErrorBody;
}

function toError(stage: string, response: Response | undefined, error: unknown): CredentialsError {
  const e = new Error(stage) as CredentialsError;
  if (response) e.status = response.status;
  e.body = (error ?? {}) as ErrorBody;
  return e;
}

export const credentialsQueryKey = ['security', 'credentials'] as const;

/**
 * Fetches the authenticated user's WebAuthn credentials.
 * Uses the default backend pagination limit which is plenty for v1.
 */
export function useCredentials(opts?: { enabled?: boolean }) {
  const api = useApi();
  return useQuery<CredentialItem[], CredentialsError>({
    queryKey: credentialsQueryKey,
    enabled: opts?.enabled ?? true,
    queryFn: async () => {
      const { data, error, response } = await api.GET('/auth/webauthn/credentials', {});
      if (error) throw toError('credentials_list_failed', response, error);
      const payload = data as { items?: CredentialItem[] } | undefined;
      const items = payload?.items ?? [];
      // Defensive narrowing: server returns `used_as` as `'primary' | 'mfa'`
      // but its OpenAPI type is a free-form string. Items are otherwise
      // pass-through.
      for (const c of items) {
        c.used_as = (c.used_as as CredentialItem['used_as']) ?? 'primary';
      }
      return items;
    },
  });
}

/**
 * Revokes (deletes) a WebAuthn credential. The backend gates this on
 * RequireStepUp, so a 403 step_up_required is intercepted by the
 * api-client step-up middleware (Task 1), which opens the global dialog
 * and replays the DELETE once verification succeeds.
 */
export function useDeleteCredential() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<void, CredentialsError, { id: string }>({
    mutationFn: async ({ id }) => {
      const { error, response } = await api.DELETE('/auth/webauthn/credentials/{id}', {
        params: { path: { id } },
      });
      if (error) throw toError('credential_delete_failed', response, error);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: credentialsQueryKey });
    },
  });
}
